import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  Typography,
} from '@mui/material';

import { Helmet } from 'react-helmet-async';

import api from '../../../../services/api';
import FormStyle from '../FormStyle';
import showTableData from '../Functions/showTableData';
import { useUserContext } from '../../../../contexts/UserContext';
import TwoFactorAuthModal from '../../../../components/Modals/TwoFactorAuthModal';

function Index() {
  // variables
  const { user, logoutUser } = useUserContext();
  const [userCard, setUserCard] = useState<{ last4: string } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [isAccountLocked, setIsAccountLocked] = useState(false);

  // effects
  useEffect(() => {
    setIsLoading(true);
    api.get('/v1/payment-methods')
      .then((res) => {
        setUserCard({ last4: res.data.data.last4 });
      }).finally(() => {
        setIsLoading(false);
      });
  }, [user.is_invoice_mode_enabled]);

  // render conditions
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="90vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // functions
  const handleSendTwoFactorCode = async (resend = false) => {
    if (!user.mail) {
      throw new Error('ユーザー情報が見つかりません');
    }

    try {
      await api.post('/v1/payment-methods/send-two-factor-code', {
        resend,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || '認証コードの送信に失敗しました';
      setTwoFactorError(errorMessage);
      throw error;
    }
  };

  const handleRegisterCard = async () => {
    if (!user.mail) {
      return;
    }

    setIsAccountLocked(false);
    setTwoFactorError('');
    setShowTwoFactorModal(true);

    await handleSendTwoFactorCode().catch(() => {
      // Error already handled in handleSendTwoFactorCode
    });
  };

  const handleVerifyTwoFactor = async (code: string) => {
    if (!user.mail) {
      setTwoFactorError('ユーザー情報が見つかりません');
      return;
    }

    setTwoFactorLoading(true);
    setTwoFactorError('');

    try {
      // Verify 2FA code and get billing portal URL in one request
      const response = await api.post('/v1/payment-methods/billing-portal', {
        code: code.trim(),
      });

      // If verification successful, redirect to billing portal
      setShowTwoFactorModal(false);
      setTwoFactorError('');
      window.location.href = response.data.url;
    } catch (error: any) {
      const isAccountLockedError = error?.response?.data?.is_account_locked ?? false;

      // Check if account is locked based on backend's is_account_locked flag
      if (isAccountLockedError) {
        setIsAccountLocked(true);
        setTwoFactorError('');
        setTwoFactorLoading(false);
        // Logout user after account is locked
        setTimeout(() => {
          logoutUser();
        }, 2000); // Give user 2 seconds to see the locked message
        return;
      }

      // Handle error messages
      if (error?.response?.data?.message) {
        const errorMsg = error.response.data.message;
        if (errorMsg.includes('ログイン試行回数の上限に達したため、アカウントはロックされました。')) {
          setIsAccountLocked(true);
          setTwoFactorError('');
          // Logout user after account is locked
          setTimeout(() => {
            logoutUser();
          }, 2000); // Give user 2 seconds to see the locked message
        } else {
          setTwoFactorError(errorMsg);
        }
      } else if (error?.response?.data?.error) {
        const errorData = error.response.data.error;
        const errorText = typeof errorData === 'string' ? errorData : String(errorData);
        if (errorText.includes('ログイン試行回数の上限に達したため、アカウントはロックされました。')) {
          setIsAccountLocked(true);
          setTwoFactorError('');
          // Logout user after account is locked
          setTimeout(() => {
            logoutUser();
          }, 2000); // Give user 2 seconds to see the locked message
        } else {
          setTwoFactorError(errorText);
        }
      } else if (error?.response?.data?.errors) {
        // Handle validation errors
        const { errors: validationErrors } = error.response.data;
        const firstError = Object.values(validationErrors)[0];
        const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
        setTwoFactorError(errorMsg || '認証コードが正しくありません');
      } else {
        setTwoFactorError('認証コードが正しくありません');
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  return (
    <div className="main">
      <Helmet>
        <title>お支払方法</title>
      </Helmet>
      <main
        style={{
          overflowY: 'auto',
          height: 'calc(100vh - 65px)',
        }}
      >
        <Box className="container-fluid my-2">
          <Box sx={FormStyle.previewHeader}>
            <h1 className="text-bold">お支払方法</h1>
            <p>ご登録のお支払方法の確認・変更はこちら</p>
          </Box>
          <Box className="card">
            <Box className="card-body">
              <Grid
                container
                alignItems="center"
                justifyContent="center"
              >
                <Grid
                  item
                  xs={12}
                >
                  <Table className="table-form-setting">
                    <TableBody>
                      {showTableData({
                        ticket: 'ご登録のカード情報',
                        value: userCard?.last4 ? (
                          <>
                            <Typography
                              fontSize={15}
                            >
                              <span className="text-bold">
                                登録済：
                              </span>
                              {`末尾が ●●●●${userCard?.last4} のクレジットカード`}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            className="text-danger text-bold"
                          >
                            未登録
                          </Typography>
                        ),
                      })}
                    </TableBody>
                  </Table>
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <Typography
                    fontSize={12}
                    color="gray"
                  >
                    ※ 情報保護のため登録されたカード情報は末尾のみ表示されます
                  </Typography>
                </Grid>
                <Grid
                  item
                  className="my-3"
                >
                  <Button
                    variant="contained"
                    onClick={handleRegisterCard}
                    disabled={user.is_invoice_mode_enabled}
                  >
                    クレジットカードを登録する
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </main>

      <TwoFactorAuthModal
        show={showTwoFactorModal}
        onVerify={handleVerifyTwoFactor}
        onResendCode={() => handleSendTwoFactorCode(true)}
        onClose={() => {
          setShowTwoFactorModal(false);
          setIsAccountLocked(false);
          setTwoFactorError('');
        }}
        loading={twoFactorLoading}
        errorMessage={twoFactorError}
        isAccountLocked={isAccountLocked}
      />
    </div>
  );
}

export default Index;
